import React, { useEffect, useState } from 'react';
import {
  Stack, Title, Text, Paper, Group, Select, Badge, Loader, Code, Alert,
} from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import useAuthenticatedData from '../../hooks/useAuthenticatedData';
import useAuthenticatedPut from '../../hooks/useAuthenticatedPut';
import config from '../../config';

/**
 * Background-worker control plane — admin.service_control + admin.service_heartbeat.
 *
 * The control DB is shared between the Azure deployment and the local dev VM, so both would otherwise run
 * every worker. This screen is where you hand one from prod to local and back.
 *
 * ONE OWNER PER SERVICE, not a per-instance matrix: these workers all have singleton semantics (poll a
 * mailbox, sync an API, send alerts), so two running at once is always a bug. A single owner makes it
 * impossible to express.
 *
 * The instances column is the honest half — service_control is what you INTENDED, the heartbeats are what is
 * ACTUALLY alive. Without them the screen would just echo your own config back at you.
 */

const REFRESH_MS = 10_000;

/** A heartbeat older than this means the instance has stopped checking in (it ticks every 30s). */
const STALE_SECONDS = 90;

function stateColor(state, stale) {
  if (stale) return 'gray';
  switch (state) {
    case 'running':  return 'green';
    case 'idle':     return 'blue';
    case 'disabled': return 'yellow';
    case 'error':    return 'red';
    default:         return 'gray';
  }
}

function ago(seconds) {
  if (seconds == null) return '—';
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  return `${Math.floor(seconds / 3600)}h ago`;
}

function ServiceCard({ svc, allInstances, onSaved }) {
  const { put } = useAuthenticatedPut();
  const [saving, setSaving] = useState(false);

  const setOwner = async (owner) => {
    if (!owner || owner === svc.owner) return;
    setSaving(true);
    try {
      await put(`${config.apiBaseUrl}/Admin/services/${svc.serviceName}/owner`, { owner });
      notifications.show({
        message: owner === 'none'
          ? `${svc.serviceName} stopped everywhere.`
          : `${svc.serviceName} handed to '${owner}' — takes effect within ~30s.`,
        color: 'green', position: 'bottom-right', autoClose: 3500,
      });
      onSaved?.();
    } catch (e) {
      notifications.show({
        message: `Failed: ${e?.response?.data?.message || e.message}`,
        color: 'red', position: 'bottom-right', autoClose: 5000,
      });
    } finally {
      setSaving(false);
    }
  };

  // 'none' plus every instance that has actually checked in. Deliberately NOT a hardcoded prod/local list:
  // you cannot hand a worker to an instance that has never proved it exists.
  const options = ['none', ...allInstances];
  const owned = svc.owner && svc.owner !== 'none';

  return (
    <Paper withBorder radius="md" p="md">
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <div style={{ flex: 1, minWidth: 0 }}>
          <Group gap="xs" mb={4}>
            <Title order={4}>{svc.serviceName}</Title>
            {!owned && <Badge color="gray" variant="light">stopped</Badge>}
          </Group>
          {svc.description && <Text size="xs" c="dimmed">{svc.description}</Text>}
        </div>

        <Group gap="lg" align="flex-start" wrap="nowrap">
          <Select
            label="Owner"
            data={options}
            value={svc.owner || 'none'}
            onChange={setOwner}
            disabled={saving}
            allowDeselect={false}
            w={160}
          />

          <div style={{ minWidth: 220 }}>
            <Text size="xs" c="dimmed" mb={4}>Instances seen</Text>
            {svc.instances.length === 0 && <Text size="xs" c="dimmed">none reporting</Text>}
            {svc.instances.map((i) => {
              const stale = i.secondsSinceSeen == null || i.secondsSinceSeen > STALE_SECONDS;
              return (
                <Group key={i.instance} gap="xs" wrap="nowrap">
                  <Badge size="sm" variant="light" color={stateColor(i.state, stale)}>
                    {stale ? 'gone' : i.state}
                  </Badge>
                  <Text size="xs" fw={i.instance === svc.owner ? 700 : 400}>{i.instance}</Text>
                  <Text size="xs" c="dimmed">{ago(i.secondsSinceSeen)}</Text>
                </Group>
              );
            })}
          </div>
        </Group>
      </Group>

      {svc.instances.filter((i) => i.lastError).map((i) => (
        <Code key={i.instance} block mt="sm" color="red.0" style={{ fontSize: 11 }}>
          {i.instance}: {i.lastError}
        </Code>
      ))}
    </Paper>
  );
}

export default function ServiceControl() {
  const { data, isLoading, refetch } = useAuthenticatedData(`${config.apiBaseUrl}/Admin/services`);
  const services = Array.isArray(data) ? data : [];

  useEffect(() => {
    const t = setInterval(refetch, REFRESH_MS);
    return () => clearInterval(t);
  }, [refetch]);

  // The union of every instance that has ever checked in, across all services.
  const allInstances = [...new Set(services.flatMap((s) => s.instances.map((i) => i.instance)))].sort();

  return (
    <Stack gap="md" p="md">
      <div>
        <Title order={2}>Service control</Title>
        <Text size="sm" c="dimmed">
          Which instance runs each background worker. The control DB is shared between the Azure deployment and
          local dev, so exactly <b>one</b> owner runs a worker at a time — these all poll mailboxes, sync APIs or
          send email, and double-running is always a bug. Changes take effect within ~30s, no restart.
        </Text>
      </div>

      <Alert variant="light" color="gray" icon={<IconAlertTriangle size={16} />}>
        A worker can also be killed locally in <Code>appsettings</Code> (e.g. <Code>Vesper:EnrichmentEnabled=false</Code>),
        which wins over anything set here — that instance shows as <b>disabled</b> and will not run the worker even
        if it owns it.
      </Alert>

      {isLoading && services.length === 0 && <Group justify="center" p="xl"><Loader /></Group>}
      {services.map((s) => (
        <ServiceCard key={s.serviceName} svc={s} allInstances={allInstances} onSaved={refetch} />
      ))}
      {!isLoading && services.length === 0 && (
        <Text c="dimmed">No services registered (apply migration 43).</Text>
      )}
    </Stack>
  );
}
